import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AddSubunitDialog from './AddSubunitDialog';
import EditSubunitDialog from './EditSubunitDialog';
import api from '../utils/api';
import './AdminSubunits.css';

const AdminSubunits = () => {
  const { adminUnitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const adminUnit = location.state?.adminUnit;
  

  
  const [subunits, setSubunits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSubunit, setSelectedSubunit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedSubunit, setDraggedSubunit] = useState(null);
  const [dragOverSubunit, setDragOverSubunit] = useState(null);

  const fetchAdminUnit = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin-units');
      
      if (response.data.success) {
        const unit = response.data.data.find(item => item._id === adminUnitId);
        if (!unit) {
          throw new Error('Admin unit not found');
        }
        // Fetch subunits after getting admin unit
        const subunitsResponse = await api.get(`/admin-subunits?administration_ref=${unit._id}`);
        if (subunitsResponse.data.success) {
          setSubunits(subunitsResponse.data.data);
        }
      } else {
        throw new Error(response.data.message || 'Failed to fetch admin unit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch admin unit');
    } finally {
      setLoading(false);
    }
  }, [adminUnitId]);

  const fetchSubunits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin-subunits?administration_ref=${adminUnit._id}`);
      
      if (response.data.success) {
        setSubunits(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch subunits');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch subunits');
    } finally {
      setLoading(false);
    }
  }, [adminUnit._id]);

  useEffect(() => {
    if (adminUnit) {
      fetchSubunits();
    } else if (adminUnitId) {
      // If no adminUnit in state, fetch it from API
      fetchAdminUnit();
    }
  }, [adminUnit, adminUnitId, fetchSubunits, fetchAdminUnit]);

  const handleAddSubunit = async (subunitData) => {
    try {
      const data = {
        ...subunitData,
        administration_ref: adminUnit?._id || adminUnitId
      };
      
      const response = await api.post('/admin-subunits', data);
      
      if (response.data.success) {
        setSubunits([...subunits, response.data.data]);
        setShowAddDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to create subunit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create subunit');
    }
  };

  const handleEditSubunit = async (id, subunitData) => {
    try {
      const response = await api.put(`/admin-subunits/${id}`, subunitData);
      
      if (response.data.success) {
        setSubunits(subunits.map(subunit => 
          subunit._id === id ? { ...subunit, ...response.data.data } : subunit
        ));
        setShowEditDialog(false);
        setSelectedSubunit(null);
      } else {
        throw new Error(response.data.message || 'Failed to update subunit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update subunit');
    }
  };

  const handleDeleteSubunit = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subunit?')) {
      return;
    }

    try {
      const response = await api.delete(`/admin-subunits/${id}`);
      
      if (response.data.success) {
        setSubunits(subunits.filter(subunit => subunit._id !== id));
      } else {
        throw new Error(response.data.message || 'Failed to delete subunit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete subunit');
    }
  };

  const openEditDialog = (subunit) => {
    setSelectedSubunit(subunit);
    setShowEditDialog(true);
  };

  const openStaffs = (subunit) => {
    navigate(`/administrations/${adminUnit?._id || adminUnitId}/subunits/${subunit._id}/staffs`, {
      state: { 
        subunit: subunit,
        adminUnit: adminUnit 
      }
    });
  };

  const sendUpdatedIndices = useCallback(async (updatedSubunits) => {
    try {
      const indices = updatedSubunits.map((subunit, idx) => ({
        _id: subunit._id,
        index: idx + 1
      }));
      
      await api.put('/admin-subunits/reorder', { subunits: indices });
    } catch (err) {
      console.error('Failed to update subunit order:', err);
      setError('Failed to save new order. Please try again.');
    }
  }, []);

  const handleDragStart = (e, subunit) => {
    setDraggedSubunit(subunit);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, subunit) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedSubunit && subunit._id !== draggedSubunit._id) {
      setDragOverSubunit(subunit);
    }
  };

  const handleDrop = (e, targetSubunit) => {
    e.preventDefault();
    
    if (!draggedSubunit || draggedSubunit._id === targetSubunit._id) {
      setDraggedSubunit(null);
      setDragOverSubunit(null);
      return;
    }

    const draggedIndex = subunits.findIndex(s => s._id === draggedSubunit._id);
    const targetIndex = subunits.findIndex(s => s._id === targetSubunit._id);

    const newSubunits = [...subunits];
    const [removed] = newSubunits.splice(draggedIndex, 1);
    newSubunits.splice(targetIndex, 0, removed);

    setSubunits(newSubunits);
    setDraggedSubunit(null);
    setDragOverSubunit(null);

    // Auto-save after 1 second
    setTimeout(() => {
      sendUpdatedIndices(newSubunits);
    }, 1000);
  };

  const handleDragEnd = () => {
    setDraggedSubunit(null);
    setDragOverSubunit(null);
  };



  const filteredSubunits = subunits.filter(subunit =>
    subunit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subunit.nameEn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!adminUnit && !adminUnitId) return null;

  if (loading) {
    return (
      <div className="subunits-container">
        <div className="loading-spinner">Loading subunits...</div>
      </div>
    );
  }

  return (
    <div className="subunits-container">
      <div className="subunits-header">
        <div className="header-left">
          <h2>Subunits of {adminUnit?.name || 'Administrative Unit'}</h2>
        </div>
        <button 
          className="add-button"
          onClick={() => setShowAddDialog(true)}
        >
          + Add New Subunit
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search subunits..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="subunits-grid">
        {filteredSubunits.length === 0 ? (
          <div className="no-subunits">
            {searchTerm ? 'No subunits found matching your search.' : 'No subunits found for this administrative unit.'}
          </div>
        ) : (
          filteredSubunits.map((subunit) => (
            <div
              key={subunit._id}
              className={`subunit-card ${
                draggedSubunit?._id === subunit._id ? 'dragging' : ''
              } ${
                dragOverSubunit?._id === subunit._id ? 'drag-over' : ''
              }`}
              onClick={() => openStaffs(subunit)}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, subunit)}
              onDragOver={(e) => handleDragOver(e, subunit)}
              onDrop={(e) => handleDrop(e, subunit)}
              onDragEnd={handleDragEnd}
            >
              <div className="subunit-header">
                <div className="subunit-names">
                  <h3 className="subunit-name">{subunit.name}</h3>
                  <p className="subunit-name-en">{subunit.nameEn}</p>
                </div>
                <div className="subunit-actions">
                  <div className="drag-icon-container" title="Drag to reorder">
                    ⋮⋮
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(subunit);
                    }}
                    className="edit-button"
                    title="Edit Subunit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSubunit(subunit._id);
                    }}
                    className="delete-button"
                    title="Delete Subunit"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <div className="subunit-footer">
                <span className="view-staff-hint">👥 Click to view staff members</span>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddDialog && (
        <AddSubunitDialog
          onClose={() => setShowAddDialog(false)}
          onAdd={handleAddSubunit}
          adminUnitName={adminUnit?.name || 'Administrative Unit'}
        />
      )}

      {showEditDialog && selectedSubunit && (
        <EditSubunitDialog
          subunit={selectedSubunit}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedSubunit(null);
          }}
          onEdit={handleEditSubunit}
        />
      )}
    </div>
  );
};

export default AdminSubunits;
